import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { LockConflictError } from '@/lib/locks/types';
import { useCareTopUp } from '@/lib/disruptions/use-disruptions';
import type { CareBoard, CareReasonCode } from '@/lib/disruptions/types';
import { formatEntitlement, formatMinor, type CareDeskRow } from './care-desk-model';

// Discretionary top-up dialog (feat/care-desk K6, NEX-458) — moves REAL money
// onto the GROUP's prepaid card, so the copy is honest about the instrument
// (one card per group, held by the lead) and consequence-first about the money.
// The write is the K5 seam (`useCareTopUp`, Idempotency-Key handled there); 202
// closes the dialog — the card strip's sky "settling" chip (WS `CareChanged`)
// carries the story from there. NEVER fabricate the new balance, NEVER retry a
// POST automatically; any failure keeps the dialog open with "nothing was saved".
//
// The cap gate: cap + granted-today come from the board/row when the BFF exposes
// them (read defensively — the K4 wire contract is frozen without them), else we
// stay optimistic and let the BFF 403 `cap_exceeded` be the authority. Over-cap
// either way surfaces the amber POL-14 waiver box naming the exceeded rule.

/** Local preset fallback when the board doesn't expose the policy knobs (K2).
 *  Keys, never language — the dialog resolves them through the `care` bundle
 *  (board-supplied presets carry their own server label instead). */
const DEFAULT_PRESETS: ReadonlyArray<{ labelKey: string; amount: number }> = [
  { labelKey: 'topUp.preset.breakfast', amount: 15 },
  { labelKey: 'topUp.preset.lunch', amount: 25 },
  { labelKey: 'topUp.preset.dinner', amount: 30 },
  { labelKey: 'topUp.preset.overnightWait', amount: 40 },
];

const REASON_CODES: readonly CareReasonCode[] = [
  'SPECIAL_NEED',
  'AIRPORT_WAIT_MEAL',
  'DELAY_EXTENSION',
  'ACCESSIBILITY',
  'GOODWILL',
  'ERROR_CORRECTION',
];

const REASON_LABEL_KEY: Record<CareReasonCode, string> = {
  SPECIAL_NEED: 'topUp.reasonCodes.specialNeed',
  AIRPORT_WAIT_MEAL: 'topUp.reasonCodes.airportWaitMeal',
  DELAY_EXTENSION: 'topUp.reasonCodes.delayExtension',
  ACCESSIBILITY: 'topUp.reasonCodes.accessibility',
  GOODWILL: 'topUp.reasonCodes.goodwill',
  ERROR_CORRECTION: 'topUp.reasonCodes.errorCorrection',
};

export interface CareTopUpPrefill {
  amount?: number;
  reasonCode?: CareReasonCode;
  reason?: string;
}


/** The 403 `cap_exceeded` body the BFF cap gate returns (authoritative). */
interface ICapRefusal {
  capPerPnrDay?: number;
  grantedToday?: number;
}

/** Duck-typed 403 reader (ky HTTPError or anything response-shaped) — returns
 *  the cap body when the refusal is the cap gate, `{}` for a bare role 403. */
async function capRefusalOf(err: unknown): Promise<ICapRefusal | null> {
  const resp = (
    err as { response?: { status?: number; clone?: () => { json: () => Promise<unknown> } } }
  )?.response;
  if (!resp || resp.status !== 403) return null;
  try {
    const body = (await resp.clone?.().json()) as
      | ({ error?: string } & ICapRefusal)
      | undefined;
    if (body?.error === 'cap_exceeded') return body;
  } catch {
    // unreadable body — still a 403 refusal
  }
  return {};
}

export function CareTopUpDialog({
  desk,
  disruptionUrn,
  board,
  prefill,
  onClose,
}: {
  desk: CareDeskRow;
  disruptionUrn: string;
  board: CareBoard | null;
  prefill?: CareTopUpPrefill;
  onClose: () => void;
}) {
  const { t } = useTranslation('care');
  const { row, card } = desk;
  // The card's currency is LAW — the wallet 422s on a mismatch, so the dialog
  // never offers a currency choice.
  const currency =
    card?.currency ?? row.mealVoucher?.currency ?? row.careSuggestedGrant?.currency ?? 'USD';

  // Presets: the board's policy knobs (K2 — presets come FROM POLICY through
  // the BFF), else the local defaults for boards that predate the field.
  const knobs = board?.knobs;
  const presets: ReadonlyArray<{ label: string; amount: number }> = knobs?.topUpPresets?.length
    ? knobs.topUpPresets
    : DEFAULT_PRESETS.map((p) => ({ label: t(p.labelKey), amount: p.amount }));

  const [amountRaw, setAmountRaw] = useState<string>(
    prefill?.amount !== undefined ? String(prefill.amount) : '',
  );
  const [activePreset, setActivePreset] = useState<number | null>(null);
  const [reasonCode, setReasonCode] = useState<CareReasonCode | ''>(prefill?.reasonCode ?? '');
  const [reason, setReason] = useState(prefill?.reason ?? '');
  const [waiveCap, setWaiveCap] = useState(false);
  const [capRefusal, setCapRefusal] = useState<ICapRefusal | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const topUp = useCareTopUp(disruptionUrn);
  const amount = Number(amountRaw);
  const amountValid = Number.isFinite(amount) && amount > 0;

  // Optimistic local cap check — cap + granted-today only when the wire exposes
  // them (defensive read); absent → no local box, the BFF 403 is authoritative.
  const localCap = knobs?.topUpCapPerPnrDay;
  const grantedToday = (card as unknown as { grantedToday?: number } | undefined)?.grantedToday;
  const overCapLocal =
    typeof localCap === 'number' && amountValid && amount + (grantedToday ?? 0) > localCap;
  const showWaiver = overCapLocal || capRefusal !== null;
  const capLine =
    capRefusal && typeof capRefusal.capPerPnrDay === 'number'
      ? { cap: capRefusal.capPerPnrDay, granted: capRefusal.grantedToday ?? 0 }
      : typeof localCap === 'number'
        ? { cap: localCap, granted: grantedToday ?? 0 }
        : null;

  const walletKnown = board?.known.wallet !== false;
  const availableLabel =
    walletKnown && card?.availableMinor !== undefined
      ? t('topUp.available', { amount: formatMinor(card.availableMinor, card.currency) })
      : t('balancesNotRead');

  const valid = amountValid && reasonCode !== '' && reason.trim() !== '' && !topUp.isPending;
  const addLabel = amountValid ? formatMinor(amount * 100, currency) : formatMinor(0, currency);

  const pickPreset = (index: number, presetAmount: number): void => {
    setActivePreset(index);
    setAmountRaw(String(presetAmount));
  };

  const onCustomAmount = (value: string): void => {
    setAmountRaw(value);
    setActivePreset(null); // custom input deselects the chips
  };

  const submit = async (): Promise<void> => {
    setFailure(null);
    try {
      await topUp.mutateAsync({
        subCaseUrn: row.subCaseUrn,
        caseUrn: row.caseUrn,
        amount,
        currency,
        reasonCode: reasonCode as CareReasonCode,
        reason: reason.trim(),
        ...(waiveCap ? { waiveCap: true } : {}),
      });
      // 202 — the money settles async; the card strip's "settling" chip takes
      // over. We NEVER show a fabricated new balance.
      onClose();
    } catch (err) {
      if (err instanceof LockConflictError) {
        setFailure(
          t('topUp.failure.lockConflict', {
            holder: err.holder?.displayName ?? t('common:holds.lock.anotherOperator'),
          }),
        );
        return;
      }
      const refusal = await capRefusalOf(err);
      if (refusal) {
        // 403 cap_exceeded (or a bare role 403): the dialog STAYS OPEN with the
        // who-can copy; the waiver box appears so a supervisor can declare it.
        setCapRefusal(refusal);
        return;
      }
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        setFailure(t('topUp.failure.notActive'));
      } else if (status === 422) {
        setFailure(t('topUp.failure.currency'));
      } else {
        setFailure(t('topUp.failure.generic'));
      }
    }
  };

  const presetChips = useMemo(
    () =>
      presets.map((p, i) => ({
        ...p,
        chipLabel: t('topUp.presetChip', {
          amount: formatEntitlement(p.amount, currency),
          label: p.label,
        }),
        index: i,
      })),
    [presets, currency, t],
  );

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-w-md" data-testid="care-top-up-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('topUp.title', { last4: card?.last4 ?? '····' })}{' '}
            <span className="font-mono">{row.pnrLabel}</span>
          </DialogTitle>
          {/* Honest about the instrument: ONE card per group, held by the lead. */}
          <DialogDescription data-testid="top-up-subtitle">
            {t('topUp.subtitle', {
              holder: card?.holderMasked ?? row.leadName ?? t('topUp.theLead'),
              balance: availableLabel,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('topUp.presetsLabel')}>
            {presetChips.map((p) => (
              <button
                key={p.index}
                type="button"
                aria-pressed={activePreset === p.index}
                onClick={() => pickPreset(p.index, p.amount)}
                data-testid={`top-up-preset-${p.index}`}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  activePreset === p.index
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground',
                )}
              >
                {p.chipLabel}
              </button>
            ))}
            <button
              type="button"
              aria-pressed={activePreset === null && amountRaw !== ''}
              onClick={() => {
                setActivePreset(null);
                setAmountRaw('');
              }}
              data-testid="top-up-preset-custom"
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                activePreset === null && amountRaw !== ''
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground',
              )}
            >
              {t('topUp.custom')}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-xs uppercase text-muted-foreground">
                {t('topUp.fields.amount')}
              </span>
              <input
                type="number"
                min="0"
                step="1"
                inputMode="decimal"
                value={amountRaw}
                onChange={(e) => onCustomAmount(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 tabular-nums"
                aria-label={t('topUp.fields.amountAria')}
                data-testid="top-up-amount"
              />
            </label>
            <div className="space-y-1">
              <span className="block text-xs uppercase text-muted-foreground">
                {t('topUp.fields.currency')}
              </span>
              {/* LOCKED to the card's currency — the wallet 422s on a mismatch. */}
              <p
                className="rounded-md border border-input bg-muted/40 px-2 py-1.5 text-muted-foreground"
                data-testid="top-up-currency"
              >
                {currency}{' '}
                <span className="text-[11px]">{t('topUp.fields.currencyNote')}</span>
              </p>
            </div>
          </div>

          <label className="block space-y-1">
            <span className="text-xs uppercase text-muted-foreground">
              {t('topUp.fields.reasonCode')}
            </span>
            <select
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value as CareReasonCode | '')}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5"
              data-testid="top-up-reason-code"
            >
              <option value="">{t('topUp.fields.reasonCodePlaceholder')}</option>
              {REASON_CODES.map((code) => (
                <option key={code} value={code}>
                  {t(REASON_LABEL_KEY[code])}
                </option>
              ))}
            </select>
          </label>

          <label className="block space-y-1">
            <span className="text-xs uppercase text-muted-foreground">
              {t('topUp.fields.reason')}
            </span>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5"
              placeholder={t('topUp.fields.reasonPlaceholder')}
              data-testid="top-up-reason"
            />
            <span className="block text-[11px] text-muted-foreground">
              {t('topUp.fields.reasonHint')}
            </span>
          </label>

          {showWaiver ? (
            // POL-14: over-cap is a knowing policy waiver — amber box naming the
            // exceeded rule, supervisor-gated by the BFF.
            <div
              className="space-y-1.5 rounded-md border border-amber-300/50 bg-amber-50/50 p-2 dark:bg-amber-950/20"
              data-testid="top-up-waiver-box"
            >
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={waiveCap}
                  onChange={(e) => setWaiveCap(e.target.checked)}
                  className="mt-0.5"
                  data-testid="top-up-waive-cap"
                />
                <span>
                  {t('topUp.waiver.prefix')} <strong>{t('topUp.waiver.rule')}</strong>{' '}
                  {t('topUp.waiver.suffix')}
                </span>
              </label>
              {capLine ? (
                <p className="pl-6 text-[11px] text-amber-800 dark:text-amber-300">
                  {t('topUp.waiver.capLine', {
                    cap: formatEntitlement(capLine.cap, currency),
                    granted: formatEntitlement(capLine.granted, currency),
                  })}
                </p>
              ) : null}
            </div>
          ) : null}

          {capRefusal ? (
            <p
              className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-sm text-destructive"
              role="alert"
              data-testid="top-up-cap-refused"
            >
              {t('topUp.capRefused')}
            </p>
          ) : null}

          {failure ? (
            <p className="text-xs text-destructive" role="alert" data-testid="top-up-failure">
              {failure}
            </p>
          ) : null}

          {/* Consequence-first footer: what happens, when, and who's on record. */}
          <p className="text-xs text-muted-foreground" data-testid="top-up-consequence">
            {t('topUp.consequence', { amount: addLabel })}
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={topUp.isPending}>
            {t('topUp.keep')}
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={!valid}
            data-testid="top-up-submit"
          >
            {topUp.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : null}
            {t('topUp.submit', { amount: addLabel })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
